import { useEffect, useState } from 'react'
import { CalendarDays } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

// Campo de data sempre exibido em dd/mm/aaaa (independente do locale do navegador).
// Guarda/emite ISO (yyyy-mm-dd). Digita-se direto (com máscara) e há um seletor
// de calendário nativo embutido no ícone.
function isoToBr(iso: string) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '')
  return m ? `${m[3]}/${m[2]}/${m[1]}` : ''
}
function brToIso(br: string): string | null {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(br)
  if (!m) return null
  const d = +m[1],
    mo = +m[2],
    y = +m[3]
  if (mo < 1 || mo > 12 || d < 1 || d > 31 || y < 1900) return null
  // Rejeita datas impossíveis (ex.: 31/02): reconstrói e confere o round-trip,
  // senão "2026-02-31" rolaria silenciosamente para março.
  const dt = new Date(y, mo - 1, d)
  if (dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== d) return null
  return `${m[3]}-${m[2]}-${m[1]}`
}
function mask(s: string) {
  const g = s.replace(/\D/g, '').slice(0, 8)
  let out = g.slice(0, 2)
  if (g.length >= 3) out += '/' + g.slice(2, 4)
  if (g.length >= 5) out += '/' + g.slice(4, 8)
  return out
}

export function DateInput({
  value,
  onChange,
  disabled,
  id,
  className,
  onBlur,
  onKeyDown,
}: {
  value: string
  onChange: (iso: string) => void
  disabled?: boolean
  id?: string
  className?: string
  /** Chamado ao sair do campo (menos quando o foco vai para o calendário interno).
   *  Usado na edição inline para cancelar ao clicar fora sem travar a célula. */
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
}) {
  const [text, setText] = useState(isoToBr(value))
  useEffect(() => {
    setText(isoToBr(value))
  }, [value])

  return (
    <div className="relative" data-dateinput>
      <Input
        id={id}
        value={text}
        inputMode="numeric"
        placeholder="dd/mm/aaaa"
        disabled={disabled}
        className={cn('pr-9', className)}
        onKeyDown={onKeyDown}
        onBlur={(e) => {
          // Não dispara ao abrir o calendário nativo (o foco vai para o <input
          // type=date> irmão, dentro do mesmo wrapper) — senão a célula fecharia
          // antes de o usuário escolher a data.
          const to = e.relatedTarget as HTMLElement | null
          if (to && to.closest?.('[data-dateinput]')) return
          onBlur?.(e)
        }}
        onChange={(e) => {
          const masked = mask(e.target.value)
          setText(masked)
          if (masked === '') onChange('')
          else {
            const iso = brToIso(masked)
            if (iso) onChange(iso)
          }
        }}
      />
      {/* input nativo transparente sobre o ícone: abre o calendário do sistema */}
      <input
        type="date"
        value={value}
        disabled={disabled}
        tabIndex={-1}
        aria-hidden
        onChange={(e) => onChange(e.target.value)}
        className="absolute inset-y-0 right-0 w-9 cursor-pointer opacity-0 disabled:cursor-not-allowed"
      />
      <CalendarDays className="text-muted-foreground pointer-events-none absolute top-1/2 right-2.5 size-4 -translate-y-1/2" />
    </div>
  )
}
